<template>
  <div
    v-if="show"
    id="dev-DistanceConfirmationModal"
    class="location-modal-backdrop"
  >
    <div class="location-modal">
      <div class="location-modal-content">
        <div class="location-modal-icon warning">
          <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
        </div>
        <h2 class="location-modal-title">
          {{ $t('map_navigation.far_from_terminal_title', { distance }) }}
        </h2>
        <p class="location-modal-subtitle">
          {{ $t('map_navigation.far_from_terminal_confirm') }}
        </p>
        <p
          v-if="token"
          class="location-modal-token"
        >
          <strong>Token do terminal:</strong>
          <code class="token-value">{{ token }}</code>
        </p>
        <div class="location-modal-actions">
          <BaseButton
            bare
            variant=""
            class="btn-location-allow"
            :aria-label="$t('map_navigation.continue')"
            @click="$emit('confirm')"
          >
            {{ $t('map_navigation.continue') }}
          </BaseButton>
          <BaseButton
            bare
            variant=""
            class="btn-location-skip"
            :aria-label="$t('map_navigation.cancel')"
            @click="$emit('cancel')"
          >
            {{ $t('map_navigation.cancel') }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import BaseButton from '@/components/ui/BaseButton.vue'
  import { lockScroll, unlockScroll } from '@/utils/scrollLock'

  export default {
    name: 'DistanceConfirmationModal',
    components: {
      BaseButton
    },
    props: {
      show: {
        type: Boolean,
        default: false
      },
      distance: {
        type: String,
        default: ''
      },
      token: {
        type: String,
        default: ''
      }
    },
    emits: ['confirm', 'cancel'],
    watch: {
      show(val) {
        val ? lockScroll() : unlockScroll()
      }
    },
    beforeUnmount() {
      if (this.show) unlockScroll()
    }
  }
</script>

<style scoped lang="scss">
  .location-modal-backdrop {
    position: fixed;
    top: var(--app-fixed-top-offset, 0px);
    bottom: var(--app-fixed-bottom-offset, 0px);
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  }

  .location-modal {
    position: relative;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    width: 85%;
    max-width: 400px;
    animation: slideUp 0.3s ease;

    @keyframes slideUp {
      from {
        transform: translateY(20px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @media (max-width: 768px) {
      width: 90%;
      max-width: 350px;
    }

    @media (max-width: 480px) {
      width: 95%;
      max-width: 95%;
      border-radius: 12px;
    }
  }

  .location-modal-content {
    position: relative;
    padding: 28px 24px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    box-sizing: border-box;
    width: 100%;

    @media (max-width: 768px) {
      padding: 24px 20px;
      gap: 12px;
    }

    @media (max-width: 480px) {
      padding: 20px 16px;
      gap: 10px;
    }
  }

  .location-modal-close {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  .location-modal-icon {
    font-size: 56px;

    &.warning {
      color: #ff6b35;
      animation: wobble 0.6s ease;

      @keyframes wobble {
        0%,
        100% {
          transform: rotate(0deg);
        }
        25% {
          transform: rotate(-2deg);
        }
        75% {
          transform: rotate(2deg);
        }
      }
    }

    @media (max-width: 768px) {
      font-size: 48px;
    }

    @media (max-width: 480px) {
      font-size: 40px;
    }
  }

  .location-modal-title {
    font-size: 20px;
    font-weight: 700;
    color: #424242;
    margin: 0;
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: 18px;
    }

    @media (max-width: 480px) {
      font-size: 16px;
    }
  }

  .location-modal-description {
    font-size: 14px;
    color: #666;
    margin: 0;
    line-height: 1.5;

    @media (max-width: 768px) {
      font-size: 13px;
    }

    @media (max-width: 480px) {
      font-size: 12px;
    }
  }

  .location-modal-subtitle {
    font-size: 12px;
    color: #999;
    margin: 0;
    line-height: 1.4;
    font-style: italic;

    @media (max-width: 768px) {
      font-size: 11px;
    }

    @media (max-width: 480px) {
      font-size: 10px;
    }
  }

  .location-modal-token {
    font-size: 14px;
    color: #424242;
    margin: 0;
    line-height: 1.2;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .token-value {
    display: inline-block;
    margin-top: 6px;
    padding: 10px 14px;
    background: linear-gradient(90deg, #fff4e6, #fff5e0);
    border-radius: 10px;
    font-family: monospace;
    font-size: 18px;
    font-weight: 700;
    color: #e65100;
    border: 1px solid rgba(230, 81, 0, 0.15);
    box-shadow: 0 6px 18px rgba(230, 81, 0, 0.08);
    letter-spacing: 1px;
    white-space: nowrap;
  }

  .location-modal-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    margin-top: 12px;
    max-width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
      gap: 8px;
      margin-top: 10px;
    }

    @media (max-width: 480px) {
      gap: 8px;
      margin-top: 8px;
    }
  }

  .btn-location-allow,
  .btn-location-skip {
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: var(--fs-sm);
    font-weight: var(--fw-semibold);
    letter-spacing: -0.01em;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
      padding: 11px 16px;
      font-size: var(--fs-xs);
    }

    @media (max-width: 480px) {
      padding: 10px 14px;
      font-size: 12px;
      border-radius: 6px;
    }
  }

  .btn-location-allow {
    background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(39, 174, 96, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(39, 174, 96, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .btn-location-skip {
    background: #f5f5f5;
    color: #666;
    border: 1px solid #e0e0e0;

    &:hover {
      background: #eeeeee;
      border-color: #d0d0d0;
    }

    &:active {
      background: #e8e8e8;
    }
  }
</style>
